import { useMutation, useQueryClient } from '@tanstack/react-query';

import { agentLogsQueryKeys } from '~/api/agentLogs';
import type {
  AgentExecutionDetail,
  CollectTestCasePayload
} from '~/api/agentLogs/types';
import {
  createExperiment,
  getDatasets,
  isNeedsRefetchError,
  updateExperiment,
  updateExperimentEvalValue,
  type Experiment
} from '~/api/experiments';
import { agentDatasetsForCollectQueryKey } from '~/scenes/Monitor/scenes/AgentLogs/hooks/useAgentDatasetsForCollect';
import { normalizeDatasetExperiments } from '~/scenes/Monitor/scenes/AgentLogs/utils/normalizeDatasetExperiments';
import { parsePipelineVersion } from '~/scenes/Monitor/scenes/AgentLogs/utils/parsePipelineVersion';

const REFETCH_AFTER_CREATE_DELAY_MS = 800;

type UseCollectAgentExecutionParams = {
  realm: string;
  detail: AgentExecutionDetail | undefined;
  datasetId: string | null;
};

export type CollectAgentExecutionInput =
  | {
      mode: 'create';
      payload: CollectTestCasePayload;
    }
  | {
      mode: 'update';
      experimentId: string;
      payload: CollectTestCasePayload;
    };

async function persistExpectedResults({
  realm,
  pipelineName,
  pipelineVersion,
  connectorId,
  datasetId,
  experimentId,
  expectedResults
}: {
  realm: string;
  pipelineName: string;
  pipelineVersion: number;
  connectorId: string;
  datasetId: string;
  experimentId: string;
  expectedResults: CollectTestCasePayload['expectedResults'];
}) {
  if (!expectedResults?.length) {
    return;
  }

  await expectedResults.reduce(async (previous, { evalId, expectedResult }) => {
    await previous;
    await updateExperimentEvalValue(
      realm,
      pipelineName,
      pipelineVersion,
      connectorId,
      datasetId,
      experimentId,
      evalId,
      expectedResult
    );
  }, Promise.resolve());
}

async function resolveCreatedExperimentByName({
  realm,
  pipelineName,
  pipelineVersion,
  connectorId,
  datasetId,
  name
}: {
  realm: string;
  pipelineName: string;
  pipelineVersion: number;
  connectorId: string;
  datasetId: string;
  name: string;
}): Promise<Experiment | undefined> {
  await new Promise(resolve =>
    setTimeout(resolve, REFETCH_AFTER_CREATE_DELAY_MS)
  );

  const datasets = await getDatasets(
    realm,
    pipelineName,
    pipelineVersion,
    connectorId
  );
  const dataset = datasets.find(item => item.id === datasetId);
  const normalizedName = name.trim().toLowerCase();
  const match = normalizeDatasetExperiments(dataset?.experiments).find(
    experiment => experiment.name.trim().toLowerCase() === normalizedName
  );

  if (!match) {
    return undefined;
  }

  const now = Date.now();
  return {
    id: match.id,
    datasetId,
    name: match.name,
    variables: match.variables,
    enabled: true,
    createdAt: now,
    updatedAt: now,
    expectedResults: match.expectedResults.map(item => ({
      id: item.evalId,
      evalId: item.evalId,
      expectedResult: item.expectedResult
    }))
  };
}

export function useCollectAgentExecution({
  realm,
  detail,
  datasetId
}: UseCollectAgentExecutionParams) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: agentLogsQueryKeys.collect(),
    mutationFn: async (input: CollectAgentExecutionInput) => {
      if (!detail || !datasetId) {
        throw new Error('Detail and dataset are required to collect execution');
      }

      if (!input.payload.name.trim()) {
        throw new Error('Test case name is required');
      }

      const pipelineVersion = parsePipelineVersion(detail.pipelineVersion);

      if (input.mode === 'update') {
        const updated = await updateExperiment(
          realm,
          detail.pipelineName,
          pipelineVersion,
          detail.connectorId,
          datasetId,
          input.experimentId,
          {
            name: input.payload.name,
            variables: input.payload.variables,
            enabled: true
          }
        );

        await persistExpectedResults({
          realm,
          pipelineName: detail.pipelineName,
          pipelineVersion,
          connectorId: detail.connectorId,
          datasetId,
          experimentId: input.experimentId,
          expectedResults: input.payload.expectedResults
        });

        return updated;
      }

      let created: Experiment;
      try {
        created = await createExperiment(
          realm,
          detail.pipelineName,
          pipelineVersion,
          detail.connectorId,
          datasetId,
          {
            name: input.payload.name,
            variables: input.payload.variables,
            enabled: true
          }
        );
      } catch (error) {
        if (!isNeedsRefetchError(error)) {
          throw error;
        }

        const recovered = await resolveCreatedExperimentByName({
          realm,
          pipelineName: detail.pipelineName,
          pipelineVersion,
          connectorId: detail.connectorId,
          datasetId,
          name: input.payload.name
        });

        if (!recovered?.id) {
          throw error;
        }

        created = recovered;
      }

      await persistExpectedResults({
        realm,
        pipelineName: detail.pipelineName,
        pipelineVersion,
        connectorId: detail.connectorId,
        datasetId,
        experimentId: created.id,
        expectedResults: input.payload.expectedResults
      });

      return created;
    },
    onSuccess: async () => {
      if (!detail) {
        return;
      }

      const version = parsePipelineVersion(detail.pipelineVersion);
      await queryClient.invalidateQueries({
        queryKey: agentDatasetsForCollectQueryKey({
          realm,
          pipelineName: detail.pipelineName,
          pipelineVersion: detail.pipelineVersion,
          connectorId: detail.connectorId,
          version
        })
      });
    }
  });
}
